import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Inbox, Loader2, Trash2, Mail, Phone, Building2, Calendar, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "in_review", label: "In Review" },
  { value: "replied", label: "Replied" },
  { value: "closed", label: "Closed" },
];

const STATUS_BADGE = {
  new: "bg-[var(--rsi-purple)] text-white",
  in_review: "bg-amber-500 text-white",
  replied: "bg-[var(--rsi-green)] text-white",
  closed: "bg-gray-400 text-white",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminInquiries() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const { data: inquiries = [], isLoading } = useQuery({
    queryKey: ["admin-inquiries"],
    queryFn: async () => (await api.get("/admin/inquiries")).data,
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, body }) => (await api.put(`/admin/inquiries/${id}`, body)).data,
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["admin-inquiries"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      setSelected(updated);
      toast.success("Inquiry updated");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/admin/inquiries/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-inquiries"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      setSelected(null);
      toast.success("Inquiry deleted");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const filtered = filter === "all" ? inquiries : inquiries.filter((i) => i.status === filter);

  return (
    <div data-testid="admin-inquiries">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="badge-purple">
            <Inbox className="h-3.5 w-3.5" /> Trade Desk
          </span>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)]">
            Inquiries & RFQs
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { v: "all", l: "All" },
            ...STATUS_OPTIONS,
          ].map((s) => (
            <button
              key={s.v || s.value}
              onClick={() => setFilter(s.v || s.value)}
              data-testid={`inquiry-filter-${s.v || s.value}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                filter === (s.v || s.value)
                  ? "bg-[var(--rsi-purple)] text-white"
                  : "bg-white border border-gray-200 text-gray-700"
              }`}
            >
              {s.l || s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rsi-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--rsi-bg-subtle)] text-left">
              <tr>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">From</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Subject</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Status</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Received</th>
                <th className="p-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center">
                    <Loader2 className="h-5 w-5 animate-spin inline text-[var(--rsi-purple)]" />
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-gray-500">
                    No inquiries match this filter.
                  </td>
                </tr>
              ) : (
                filtered.map((i) => (
                  <tr
                    key={i.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelected(i)}
                    data-testid={`inquiry-row-${i.id}`}
                  >
                    <td className="p-4">
                      <div className="font-semibold text-[var(--rsi-navy)]">{i.company_name}</div>
                      <div className="text-xs text-gray-500">{i.contact_name} · {i.email}</div>
                    </td>
                    <td className="p-4">
                      <div className="text-gray-700 line-clamp-1 max-w-md">
                        {i.product_name ? `RFQ: ${i.product_name}` : i.message?.slice(0, 80)}
                      </div>
                      {i.country && <div className="text-xs text-gray-500 mt-0.5">→ {i.country}</div>}
                    </td>
                    <td className="p-4">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider ${STATUS_BADGE[i.status]}`}>
                        {i.status}
                      </span>
                    </td>
                    <td className="p-4 text-xs text-gray-500 font-mono-plex">{formatDate(i.created_at)}</td>
                    <td className="p-4 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm("Delete this inquiry?")) delMut.mutate(i.id);
                        }}
                        className="h-9 w-9 rounded-lg grid place-items-center hover:bg-red-50 text-gray-600 hover:text-red-600"
                        data-testid={`delete-inquiry-${i.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="inquiry-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-extrabold tracking-tight text-[var(--rsi-navy)]">
                  {selected.product_name ? `RFQ — ${selected.product_name}` : "General Inquiry"}
                </DialogTitle>
              </DialogHeader>

              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Company</div>
                  <div className="flex items-center gap-2 mt-1 text-[var(--rsi-navy)] font-medium">
                    <Building2 className="h-4 w-4 text-gray-400" /> {selected.company_name}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Contact</div>
                  <div className="text-[var(--rsi-navy)] font-medium mt-1">{selected.contact_name}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Email</div>
                  <a href={`mailto:${selected.email}`} className="flex items-center gap-2 mt-1 text-[var(--rsi-purple)] font-medium">
                    <Mail className="h-4 w-4" /> {selected.email}
                  </a>
                </div>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Phone</div>
                  <div className="flex items-center gap-2 mt-1 text-[var(--rsi-navy)] font-medium">
                    <Phone className="h-4 w-4 text-gray-400" /> {selected.phone || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Country</div>
                  <div className="text-[var(--rsi-navy)] font-medium mt-1">{selected.country || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Quantity</div>
                  <div className="text-[var(--rsi-navy)] font-medium mt-1 font-mono-plex">{selected.quantity || "—"}</div>
                </div>
              </div>

              <div className="mt-6">
                <div className="text-xs text-gray-400 uppercase tracking-wider">Message</div>
                <div className="mt-2 p-4 rounded-lg bg-[var(--rsi-bg-subtle)] text-gray-700 leading-relaxed whitespace-pre-line">
                  {selected.message}
                </div>
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider mb-1.5">Status</div>
                  <Select
                    value={selected.status}
                    onValueChange={(v) => updateMut.mutate({ id: selected.id, body: { status: v } })}
                  >
                    <SelectTrigger data-testid="inquiry-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider mb-1.5">Received</div>
                  <div className="flex items-center gap-2 mt-2 text-sm font-mono-plex text-gray-600">
                    <Calendar className="h-4 w-4 text-gray-400" /> {formatDate(selected.created_at)}
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1.5">Admin Notes</div>
                <Textarea
                  rows={3}
                  value={selected.admin_notes || ""}
                  onChange={(e) => setSelected({ ...selected, admin_notes: e.target.value })}
                  placeholder="Internal notes…"
                  data-testid="inquiry-notes"
                />
                <button
                  className="btn-ghost mt-3"
                  onClick={() => updateMut.mutate({ id: selected.id, body: { admin_notes: selected.admin_notes } })}
                  data-testid="inquiry-save-notes"
                >
                  Save notes
                </button>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                <a href={`mailto:${selected.email}?subject=Re: Your inquiry — R.S. International`} className="btn-primary">
                  Reply via Email
                </a>
                <button onClick={() => setSelected(null)} className="btn-ghost">
                  <X className="h-4 w-4" /> Close
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
